import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { Box } from "@mui/material";

import { SIDE_PANEL_MAX_WIDTH, SIDE_PANEL_MIN_WIDTH } from "defs";

import { clampWidth } from "./sidePanelWidth";

interface ResizeDrag {
  lastWidth: number;
  pointerId: number;
  previousCursor: string;
  previousUserSelect: string;
  startWidth: number;
  startX: number;
}

interface SidePanelResizeHandleProps {
  contentId: string;
  onWidthChange: (width: number) => void;
  width: number;
}

const KEYBOARD_RESIZE_STEP = 16;

const SidePanelResizeHandle: React.FC<SidePanelResizeHandleProps> = ({
  contentId,
  onWidthChange,
  width,
}) => {
  const dragRef = useRef<ResizeDrag | null>(null);

  const restoreBodyCursor = () => {
    const drag = dragRef.current;
    if (drag == null) {
      return;
    }

    document.body.style.cursor = drag.previousCursor;
    document.body.style.userSelect = drag.previousUserSelect;
  };

  useEffect(() => {
    return () => {
      const drag = dragRef.current;
      if (drag == null) {
        return;
      }

      document.body.style.cursor = drag.previousCursor;
      document.body.style.userSelect = drag.previousUserSelect;
      dragRef.current = null;
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey
      ? KEYBOARD_RESIZE_STEP * 2
      : KEYBOARD_RESIZE_STEP;

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      onWidthChange(clampWidth(width + step));
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      onWidthChange(clampWidth(width - step));
    }
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    dragRef.current = {
      lastWidth: width,
      pointerId: event.pointerId,
      previousCursor: document.body.style.cursor,
      previousUserSelect: document.body.style.userSelect,
      startWidth: width,
      startX: event.clientX,
    };
    // Pointer capture keeps events on this handle, but the cursor follows
    // whichever element is under the pointer. The handle is only a few pixels
    // wide, so once the drag leaves it the map or panel content would replace
    // the resize cursor. Pin it on the document for the gesture.
    document.body.style.cursor = "ew-resize";
    // A drag is a mousedown followed by movement, which the browser treats as
    // a text selection. Disable selection for the gesture so panel text does
    // not highlight, then restore the previous value on release.
    document.body.style.userSelect = "none";
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (drag == null || drag.pointerId !== event.pointerId) {
      return;
    }

    const nextWidth = clampWidth(
      drag.startWidth + (drag.startX - event.clientX),
    );
    if (nextWidth === drag.lastWidth) {
      return;
    }

    drag.lastWidth = nextWidth;
    onWidthChange(nextWidth);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (drag == null || drag.pointerId !== event.pointerId) {
      return;
    }

    restoreBodyCursor();
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <Box
      aria-controls={contentId}
      aria-label="Resize side panel"
      aria-orientation="vertical"
      aria-valuemax={SIDE_PANEL_MAX_WIDTH}
      aria-valuemin={SIDE_PANEL_MIN_WIDTH}
      aria-valuenow={width}
      onKeyDown={handleKeyDown}
      onPointerCancel={handlePointerUp}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="separator"
      sx={{
        bottom: 0,
        cursor: "ew-resize",
        left: 0,
        position: "absolute",
        top: 0,
        touchAction: "none",
        width: 8,
        zIndex: 1,
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      }}
      tabIndex={0}
    />
  );
};

export default SidePanelResizeHandle;
